import type { ReactNode } from 'react';

/**
 * A small, safe Markdown renderer for Linear task descriptions: headings, lists, quotes, code,
 * bold, italic, inline code and links. It builds React elements and never sets HTML, so markup
 * in the text is shown as text; links are only http(s) and mailto.
 */

const SAFE_LINK = /^(https?:\/\/|mailto:)/i;

// code | image | link | bold | italic | bare url
const INLINE = /`([^`\n]+)`|!?\[([^\]\n]*)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*|__([^_\n]+)__|(?<![\w*])\*([^*\n]+)\*(?![\w*])|(?<![\w_])_([^_\n]+)_(?![\w_])|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])/g;

function link(href: string, children: ReactNode, key: number) {
  return (
    <a key={key} href={href} target="_blank" rel="noreferrer noopener" className="underline decoration-strong underline-offset-2 hover:decoration-ink">
      {children}
    </a>
  );
}

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const key = n++;
    if (m[1] !== undefined) {
      out.push(
        <code key={key} className="rounded-sm bg-sunken px-1 font-mono text-[0.92em]">
          {m[1]}
        </code>,
      );
    } else if (m[3] !== undefined) {
      const label = m[2] || m[3];
      // An image or a link: only a safe address becomes a link, otherwise just its text.
      out.push(SAFE_LINK.test(m[3]) ? link(m[3], label, key) : label);
    } else if (m[4] !== undefined || m[5] !== undefined) {
      out.push(<strong key={key}>{renderInline((m[4] ?? m[5])!)}</strong>);
    } else if (m[6] !== undefined || m[7] !== undefined) {
      out.push(<em key={key}>{renderInline((m[6] ?? m[7])!)}</em>);
    } else if (m[8] !== undefined) {
      out.push(link(m[8], m[8], key));
    }
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { t: 'p'; text: string }
  | { t: 'h'; level: number; text: string }
  | { t: 'ul' | 'ol'; items: string[] }
  | { t: 'quote'; text: string }
  | { t: 'code'; text: string }
  | { t: 'hr' };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ t: 'p', text: para.join('\n') });
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      flush();
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++]);
      blocks.push({ t: 'code', text: body.join('\n') });
      continue;
    }
    if (!line.trim()) {
      flush();
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flush();
      blocks.push({ t: 'h', level: h[1].length, text: h[2] });
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flush();
      blocks.push({ t: 'hr' });
      continue;
    }
    const kind = BULLET.test(line) ? 'ul' : NUMBERED.test(line) ? 'ol' : null;
    if (kind) {
      flush();
      const re = kind === 'ul' ? BULLET : NUMBERED;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i])) items.push(re.exec(lines[i++])![1]);
      i--;
      blocks.push({ t: kind, items });
      continue;
    }
    if (/^\s*>/.test(line)) {
      flush();
      const body: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) body.push(lines[i++].replace(/^\s*>\s?/, ''));
      i--;
      blocks.push({ t: 'quote', text: body.join('\n') });
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
}

function lines(text: string): ReactNode[] {
  return text.split('\n').flatMap((l, i) => (i === 0 ? renderInline(l) : [<br key={`br${i}`} />, ...renderInline(l)]));
}

const task = /^\[([ xX])\]\s+/;

export function Markdown({ text, className }: { text: string; className?: string }) {
  return (
    <div className={className}>
      {parseBlocks(text).map((b, i) => {
        switch (b.t) {
          case 'h':
            return (
              <p key={i} className="mt-3 font-semibold first:mt-0">
                {renderInline(b.text)}
              </p>
            );
          case 'ul':
          case 'ol': {
            const L = b.t;
            return (
              <L key={i} className={L === 'ul' ? 'mt-2 list-disc space-y-0.5 pl-5 first:mt-0' : 'mt-2 list-decimal space-y-0.5 pl-5 first:mt-0'}>
                {b.items.map((it, j) => {
                  const box = task.exec(it);
                  return (
                    <li key={j}>
                      {box ? (box[1] === ' ' ? '☐ ' : '☑ ') : ''}
                      {renderInline(box ? it.slice(box[0].length) : it)}
                    </li>
                  );
                })}
              </L>
            );
          }
          case 'quote':
            return (
              <blockquote key={i} className="mt-2 border-l-2 border-strong pl-3 text-muted first:mt-0">
                {lines(b.text)}
              </blockquote>
            );
          case 'code':
            return (
              <pre key={i} className="mt-2 overflow-x-auto rounded bg-sunken p-3 font-mono text-xs first:mt-0">
                <code>{b.text}</code>
              </pre>
            );
          case 'hr':
            return <hr key={i} className="my-3 border-line" />;
          default:
            return (
              <p key={i} className="mt-2 first:mt-0">
                {lines(b.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
